import React, { useState, useEffect } from "react";
import * as style from "./CreateEditRoleFeatureSidePanel.module.scss";
import {
  Divider,
  Dropdown,
  DropdownOption,
  DropdownType,
  IconButton,
} from "cprime-foundational-components";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "../Common/CprimeText";
import { createMultiSelectRenderOption } from "../Common/multiSelectRenderOption";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRoleId,
  featureRelationsCreateEditPayload,
  updateFeatureRelationStatus,
} from "../../store/slices/CreateRoleSlice";
import { RootState } from "../../store/store";
import {
  getRoleSetupFeatureDropdownData,
  getAssociatedRolesByEnvironment,
} from "../../services/RequestAccess/RequestAccess";
import { getEnv } from "../../Constants/env";
import { useParams } from "react-router-dom";

interface CreateEditRoleFeatureSidePanelProps {
  isEdit: boolean;
  selectedRow?: any;
}

const CreateEditRoleFeatureSidePanel: React.FC<
  CreateEditRoleFeatureSidePanelProps
> = ({ isEdit, selectedRow }) => {
  const scssClass = style as any;
  const dispatch = useDispatch();
  const currentRoleId = useSelector(selectCurrentRoleId);
  const isPlatformApp = useSelector(
    (state: RootState) => !!state.applicationDetails?.data?.platform_app,
  );
  // Associated Groups is a CRM-only feature; other applications keep the
  // pre-existing behavior (no Associated Groups dropdown/payload field).
  const isCrmApp = useSelector(
    (state: RootState) => state.applicationDetails?.data?.app_cmdb_id === "CRM",
  );

  const rawEnvType = getEnv()?.envtype?.toLowerCase();
  const isSitAssociatedRolesEnv = rawEnvType === "sit" && isPlatformApp;

  const [featureOptions, setFeatureOptions] = useState<DropdownOption[]>([]);
  const [featureSelectedOption, setFeatureSelectedOption] = useState<
    DropdownOption[]
  >([]);
  const [permissionsOptions, setPermissionsOptions] = useState<
    DropdownOption[]
  >([]);
  const [permissionsSelectedOptions, setPermissionsSelectedOptions] = useState<
    DropdownOption[]
  >([]);
  const [associatedRoleOptions, setAssociatedRoleOptions] = useState<
    DropdownOption[]
  >([]);
  const [associatedRoleSelectedOptions, setAssociatedRoleSelectedOptions] =
    useState<DropdownOption[]>([]);
  const [associatedGroupOptions, setAssociatedGroupOptions] = useState<
    DropdownOption[]
  >([]);
  const [associatedGroupSelectedOptions, setAssociatedGroupSelectedOptions] =
    useState<DropdownOption[]>([]);
  const [environmentRolesMap, setEnvironmentRolesMap] = useState<
    Record<string, DropdownOption[]>
  >({});
  const [envConditionType, setEnvConditionType] = useState<
    Record<number, DropdownOption[]>
  >({});
  const [envConditionValues, setEnvConditionValues] = useState<
    Record<number, DropdownOption[]>
  >({});
  const [envConditionCount, setEnvConditionCount] = useState<number>(0);
  const [applicationId, setApplicationId] = useState<any>();
  const [featureData, setFeatureData] = useState<any>([]);
  const [oldFeatureId, setOldFeatureId] = useState<number | null>(null);

  const { id } = useParams();


  useEffect(() => {
    setApplicationId(id?.split("-")[1]);
  }, [id]);

  const handleFeatureChange = async (selectedFeature: DropdownOption[]) => {
    try {
      const selectedFeatureId = selectedFeature[0]?.id;
      setFeatureSelectedOption(selectedFeature);
      const selectedFeatureVal: any = featureData?.feature?.find(
        (feature: any) => feature?.featureid === Number(selectedFeatureId),
      );
      const permissionOptions = selectedFeatureVal?.permissions?.map(
        (item: any) => ({
          id: item?.id,
          label: item?.permissionname,
          value: item?.id?.toString(),
        }),
      );
      setPermissionsOptions(permissionOptions);
      setPermissionsSelectedOptions(
        permissionOptions?.filter(
          (item: DropdownOption) => item?.label?.toLowerCase() === "view",
        ),
      );
    } catch (error) {
      // console.error("Error fetching features:", error);
    }
  };

  const handlePermissionChange = (selectedPermission: DropdownOption[]) => {
    if (selectedPermission?.length > permissionsOptions?.length) {
      setPermissionsSelectedOptions([]);
    } else if (
      selectedPermission?.length >= 1 &&
      selectedPermission?.some((item) => item.label === "All")
    ) {
      setPermissionsSelectedOptions(permissionsOptions);
    } else {
      setPermissionsSelectedOptions(
        selectedPermission?.filter((item) => item?.label !== "All"),
      );
    }
  };

  const handleAssociatedRoleChange = (selectedRole: DropdownOption[]) => {
    if (selectedRole?.length > associatedRoleOptions?.length) {
      setAssociatedRoleSelectedOptions([]);
    } else if (
      selectedRole?.length >= 1 &&
      selectedRole?.some((item) => item.label === "All")
    ) {
      setAssociatedRoleSelectedOptions(associatedRoleOptions);
    } else {
      // Filter by ID to prevent duplicate label issues
      const uniqueSelected = selectedRole?.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.id === item.id),
      );
      setAssociatedRoleSelectedOptions(
        uniqueSelected?.filter((item) => item.label !== "All"),
      );
    }
  };

  const handleAssociatedGroupChange = (selectedGroup: DropdownOption[]) => {
    if (selectedGroup?.length > associatedGroupOptions?.length) {
      setAssociatedGroupSelectedOptions([]);
    } else if (
      selectedGroup?.length >= 1 &&
      selectedGroup?.some((item) => item.label === "All")
    ) {
      setAssociatedGroupSelectedOptions(associatedGroupOptions);
    } else {
      const uniqueSelected = selectedGroup?.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.id === item.id),
      );
      setAssociatedGroupSelectedOptions(
        uniqueSelected?.filter((item) => item.label !== "All"),
      );
    }
  };

  const environmentAttributeOptions: DropdownOption[] = Object.keys(
    environmentRolesMap,
  ).map((env) => ({ id: env, label: env, value: env }));

  const getAvailableEnvironmentOptions = (index: number) => {
    const selectedEnvIds = new Set(
      Object.entries(envConditionType)
        .filter(([key, value]) => Number(key) !== index && value?.length > 0)
        .map(([, value]) => value?.[0]?.id)
        .filter(Boolean),
    );

    return environmentAttributeOptions.filter((option) => {
      const isCurrentSelection = envConditionType?.[index]?.some(
        (selected) => selected?.id === option?.id,
      );
      return isCurrentSelection || !selectedEnvIds.has(option.id);
    });
  };

  const canAddMoreEnvConditions =
    envConditionCount < environmentAttributeOptions.length &&
    getAvailableEnvironmentOptions(envConditionCount).length > 0;

  const handleRemoveEnvCondition = (index: number) => {
    setEnvConditionCount((prev) => {
      if (prev <= 1) {
        setEnvConditionType({});
        setEnvConditionValues({});
        return 0;
      }
      const newType: Record<number, DropdownOption[]> = {};
      const newValues: Record<number, DropdownOption[]> = {};
      let newIndex = 0;
      for (let i = 0; i < prev; i++) {
        if (i === index) {
          continue;
        }
        newType[newIndex] = envConditionType[i] ?? [];
        newValues[newIndex] = envConditionValues[i] ?? [];
        newIndex++;
      }
      setEnvConditionType(newType);
      setEnvConditionValues(newValues);
      return prev - 1;
    });
  };

  // Fetches roles grouped by environment for the SIT associated-roles condition UI
  useEffect(() => {
    if (!isSitAssociatedRolesEnv) {
      return;
    }
    const fetchEnvAssociatedRoles = async () => {
      try {
        const response = await getAssociatedRolesByEnvironment();
        const envData = response?.data ?? {};
        const mapped: Record<string, DropdownOption[]> = Object.keys(
          envData,
        ).reduce(
          (acc, env) => ({
            ...acc,
            [env]: (envData[env] ?? []).map((item: any) => ({
              id: item?.id,
              label: item?.label,
              group: item?.group,
              value: item?.id?.toString(),
              azure_group_name: item?.azure_group_name,
            })),
          }),
          {},
        );
        setEnvironmentRolesMap(mapped);
        if (!isEdit) {
          setEnvConditionCount(1);
        }
      } catch (error) {
        // console.error("Error fetching associated roles by environment:", error);
      }
    };
    fetchEnvAssociatedRoles();
  }, [isSitAssociatedRolesEnv, isEdit]);

  // Hydrates the environment conditions on edit by matching the flat
  // associated_groups azure_group_names against each env's roles list
  useEffect(() => {
    if (!isSitAssociatedRolesEnv || !isEdit) {
      return;
    }
    if (!Object.keys(environmentRolesMap).length) {
      return;
    }

    const associatedGroupNames: string[] = Array.isArray(
      selectedRow?.associated_groups,
    )
      ? selectedRow.associated_groups.map((name: string) => name?.trim())
      : [];

    if (!associatedGroupNames.length) {
      setEnvConditionCount((prev) => (prev > 0 ? prev : 1));
      return;
    }

    const nextType: Record<number, DropdownOption[]> = {};
    const nextValues: Record<number, DropdownOption[]> = {};
    let index = 0;

    Object.keys(environmentRolesMap).forEach((env) => {
      const matchedRoles = (environmentRolesMap[env] ?? []).filter((role: any) =>
        associatedGroupNames.includes(role?.azure_group_name?.trim()),
      );
      if (!matchedRoles.length) {
        return;
      }

      const envOption = environmentAttributeOptions.find((o) => o.id === env);
      if (!envOption) {
        return;
      }

      nextType[index] = [envOption];
      nextValues[index] = matchedRoles;
      index++;
    });

    setEnvConditionType(nextType);
    setEnvConditionValues(nextValues);
    setEnvConditionCount(Math.max(index, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSitAssociatedRolesEnv, isEdit, environmentRolesMap, selectedRow]);


  useEffect(() => {
    const fetchFeatures = async () => {
      if (applicationId) {
        try {
          dispatch(updateFeatureRelationStatus("idle"));
          if (!isEdit) {
            const currentSelections: any = {
              roleId: currentRoleId,
              featureId: "",
              permissionIds: [],
              associatedGroup: [],
            };
            if (isCrmApp) {
              currentSelections.associatedAppGroup = [];
            }
            dispatch(featureRelationsCreateEditPayload(currentSelections));
          }
          const response = await getRoleSetupFeatureDropdownData(applicationId);

          const featuresData: any = response?.data ?? [];
          setFeatureData(featuresData);
          if (featuresData) {
            const options = featuresData?.feature?.map((item: any) => ({
              id: item?.featureid,
              label: item?.featurename,
              value: item?.featureid?.toString(),
            }));
            setFeatureOptions(options);

            const associatedGroupsArray = featuresData?.associated_roles;
            if (associatedGroupsArray) {
              const rolesArray = associatedGroupsArray?.map((item: any) => ({
                id: item?.id,
                label: item?.label,
                group: item?.group,
                value: item?.id?.toString(),
                azure_group_name: item?.azure_group_name,
              }));
              setAssociatedRoleOptions(rolesArray);
              setAssociatedRoleSelectedOptions([]);
            }

            const associatedAppGroupsArray = featuresData?.associated_app_groups;
            if (isCrmApp && associatedAppGroupsArray) {
              const appGroupsArray = associatedAppGroupsArray?.map(
                (item: any) => ({
                  id: item?.id,
                  label: item?.label,
                  group: item?.group,
                  value: item?.id?.toString(),
                }),
              );
              setAssociatedGroupOptions(appGroupsArray);
              setAssociatedGroupSelectedOptions([]);
            }

            if (isEdit) {
              const selectedFeature: any = featuresData?.feature?.find(
                (feature: any) =>
                  feature?.featureid === Number(selectedRow?.feature_id),
              );
              setOldFeatureId(selectedRow?.feature_id);
              const selectedFeatureData = options.filter(
                (opt: any) => opt?.id === selectedRow?.feature_id,
              );
              setFeatureSelectedOption(selectedFeatureData);
              const permissionOptions = selectedFeature?.permissions?.map(
                (item: any) => ({
                  id: item?.id,
                  label: item?.permissionname,
                  value: item?.id?.toString(),
                }),
              );
              setPermissionsOptions(permissionOptions);
              const selectedPermissions = await permissionOptions?.filter(
                (permission: { label: string }) =>
                  selectedRow?.permissions
                    ?.split(", ")
                    ?.includes(permission?.label),
              );
              setPermissionsSelectedOptions(selectedPermissions);
              const associatedGroupsArray =
                featuresData?.associated_roles || [];

              const rolesArray = associatedGroupsArray?.map((item: any) => ({
                id: item?.id,
                label: item?.label,
                group: item?.group,
                value: item?.id?.toString(),
                azure_group_name: item?.azure_group_name,
              }));
              setAssociatedRoleOptions(rolesArray);
              const selectedAssociatedRoles = associatedGroupsArray?.filter(
                (role: { azure_group_name: string }) =>
                  selectedRow?.associated_groups?.includes(
                    role?.azure_group_name,
                  ),
              );
              setAssociatedRoleSelectedOptions(selectedAssociatedRoles);

              if (isCrmApp) {
                const appGroupsArray = associatedAppGroupsArray?.map(
                  (item: any) => ({
                    id: item?.id,
                    label: item?.label,
                    group: item?.group,
                    value: item?.id?.toString(),
                  }),
                );
                setAssociatedGroupOptions(appGroupsArray);
                const selectedAssociatedGroups = appGroupsArray?.filter(
                  (group: { label: string }) =>
                    selectedRow?.associated_app_groups?.includes(group?.label),
                );
                setAssociatedGroupSelectedOptions(selectedAssociatedGroups);
              }
            }
          } else {
            throw new Error(response?.error);
          }
        } catch (error) {
          // console.error("Error fetching features:", error);
        }
      }
    };

    fetchFeatures();
  }, [applicationId]);

  useEffect(() => {
    if (featureSelectedOption && featureSelectedOption?.length > 0) {
      const associatedGroupPayload = isSitAssociatedRolesEnv
        ? Object.entries(envConditionType).reduce(
            (acc: Record<string, any[]>, [index, typeOpt]) => {
              const envKey = typeOpt?.[0]?.id;
              if (!envKey) {
                return acc;
              }
              acc[envKey] = (envConditionValues[Number(index)] ?? []).map(
                (item) => item?.id,
              );
              return acc;
            },
            {},
          )
        : associatedRoleSelectedOptions?.map((item) => item?.id);

      const currentSelections: any = {
        roleId: currentRoleId,
        featureId: featureSelectedOption?.map((item) => item.id)[0],
        permissionIds: permissionsSelectedOptions?.map((item) => {
          return {
            id: item?.id?.toString(),
            permissionName: item?.label?.toLowerCase(),
          };
        }),
        associatedGroup: associatedGroupPayload,
      };
      if (isCrmApp) {
        currentSelections.associatedAppGroup = associatedGroupSelectedOptions?.map(
          (item) => item?.id,
        );
      }
      if (isEdit) {
        currentSelections.oldFeatureId = oldFeatureId;
        currentSelections.oldAssociatedGroup = Array.isArray(
          selectedRow?.associated_groups,
        )
          ? selectedRow.associated_groups
          : [];
      }
      dispatch(featureRelationsCreateEditPayload(currentSelections));
    }
  }, [
    featureSelectedOption,
    permissionsSelectedOptions,
    associatedRoleSelectedOptions,
    associatedGroupSelectedOptions,
    envConditionType,
    envConditionValues,
    isSitAssociatedRolesEnv,
  ]);


  return (
    <div className={scssClass.panelContent}>
      <Divider
        variant={"fullWidth"}
        sx={{
          marginBottom: "15px",
          width: "100%",
          marginLeft: "0px !important",
          backgroundColor: "#f8f8f9 !important",
        }}
      />
      <Typography className={scssClass.textDescription}>
        Feature Details
      </Typography>

      <div className={scssClass.fieldsContent}>
        <div className={scssClass.fieldGroup}>
          <Typography className={scssClass.label}>Feature</Typography>
          <Dropdown
            placeholder="Select"
            fullWidth
            optionsTextLimit={200}
            style={{ width: "100%" }}
            multiple={false}
            options={featureOptions}
            selectedOptions={featureSelectedOption}
            onChange={handleFeatureChange}
            error={featureSelectedOption?.length === 0}
            errorMessage={""}
          />
        </div>

        <div className={scssClass.fieldGroup}>
          <Typography className={scssClass.label}>Permissions</Typography>
          <Dropdown
            placeholder="Select"
            fullWidth
            optionsTextLimit={200}
            style={{ width: "100%" }}
            options={permissionsOptions}
            selectedOptions={permissionsSelectedOptions ?? []}
            onChange={handlePermissionChange}
            type={DropdownType.MultiSelect}
            disableCloseOnSelect
            renderOption={createMultiSelectRenderOption(
              permissionsSelectedOptions ?? [],
              handlePermissionChange,
            )}
            error={
              permissionsSelectedOptions?.length === 0 ||
              !permissionsSelectedOptions?.some((item) => item.label === "View")
            }
            errorMessage={
              permissionsSelectedOptions?.length === 0
                ? ""
                : "View permission is required"
            }
            selectAllText={"All"}
          />
        </div>

        <div className={scssClass.fieldGroup}>
          <Typography className={scssClass.label}>
            {isSitAssociatedRolesEnv ? "Associate Role" : "Associated Roles"}
          </Typography>
          {isSitAssociatedRolesEnv ? (
            <div className={scssClass.associateRoleSection}>
              {Array.from({ length: envConditionCount }, (_, index) => {
                const selectedEnvId = envConditionType?.[index]?.[0]?.id;
                const envRoleOptions = selectedEnvId
                  ? (environmentRolesMap[selectedEnvId] ?? [])
                  : [];
                return (
                  <div
                    className={scssClass.associateRoleSectionRow}
                    key={`env-condition-${index}`}
                  >
                    <div className={scssClass.associateRoleSectionField}>
                      <Typography className={scssClass.label}>
                        Environment
                      </Typography>
                      <Dropdown
                        selectedOptions={envConditionType?.[index] ?? []}
                        aria-label={`environment_dropdown_${index}`}
                        options={getAvailableEnvironmentOptions(index)}
                        placeholder="Select environment"
                        optionsTextLimit={200}
                        noOptionsText="No options"
                        type={DropdownType.FilterSingleSelect}
                        onChange={(option) => {
                          setEnvConditionType((prev) => ({
                            ...prev,
                            [index]: option?.length ? option : [],
                          }));
                          setEnvConditionValues((prev) => ({
                            ...prev,
                            [index]: [],
                          }));
                        }}
                        dropdownSx={{ maxWidth: "20rem" }}
                      />
                    </div>
                    <div className={scssClass.associateRoleSectionField}>
                      <Typography className={scssClass.label}>
                        Value
                      </Typography>
                      <Dropdown
                        placeholder="Select"
                        optionsTextLimit={200}
                        options={envRoleOptions}
                        selectedOptions={envConditionValues?.[index] ?? []}
                        disabled={!selectedEnvId}
                        onChange={(selected: DropdownOption[]) => {
                          if (selected?.length > envRoleOptions?.length) {
                            setEnvConditionValues((prev) => ({
                              ...prev,
                              [index]: [],
                            }));
                          } else if (
                            selected?.some((item) => item.label === "All")
                          ) {
                            setEnvConditionValues((prev) => ({
                              ...prev,
                              [index]: envRoleOptions,
                            }));
                          } else {
                            const uniqueSelected = selected?.filter(
                              (item, i, self) =>
                                i === self.findIndex((t) => t.id === item.id),
                            );
                            setEnvConditionValues((prev) => ({
                              ...prev,
                              [index]: uniqueSelected?.filter(
                                (item) => item.label !== "All",
                              ),
                            }));
                          }
                        }}
                        type={DropdownType.FilterMultiSelect}
                        selectAllText={"All"}
                        isOptionEqualToValue={(option: any, value: any) =>
                          option?.id === value?.id
                        }
                        filterOptions={(options: any, state: any) => {
                          const inputValue = state?.inputValue?.toLowerCase();
                          if (!inputValue) {
                            return options;
                          }
                          return options?.filter(
                            (option: any) =>
                              option?.label
                                ?.toLowerCase()
                                ?.includes(inputValue) ||
                              option?.group?.toLowerCase()?.includes(inputValue),
                          );
                        }}
                        dropdownSx={{ maxWidth: "20rem" }}
                      />
                    </div>
                    <IconButton
                      type="button"
                      aria-label="Remove environment"
                      data-testid={`remove-env-condition-${index}`}
                      onClick={() => handleRemoveEnvCondition(index)}
                    >
                      <CloseIcon className={scssClass.closeIcon} />
                    </IconButton>
                  </div>
                );
              })}
              <button
                type="button"
                disabled={!canAddMoreEnvConditions}
                onClick={() =>
                  setEnvConditionCount((prev) =>
                    Math.min(prev + 1, environmentAttributeOptions.length),
                  )
                }
                className={scssClass.roleConditionsAddButton}
              >
                Add Environment +
              </button>
            </div>
          ) : (
            <Dropdown
              placeholder="Select"
              fullWidth
              optionsTextLimit={200}
              style={{ width: "100%" }}
              options={associatedRoleOptions}
              selectedOptions={associatedRoleSelectedOptions}
              onChange={handleAssociatedRoleChange}
              type={DropdownType.FilterMultiSelect}
              selectAllText={"All"}
              isOptionEqualToValue={(option: any, value: any) =>
                option?.id === value?.id
              }
              filterOptions={(options: any, state: any) => {
                const inputValue = state?.inputValue?.toLowerCase();
                if (!inputValue) {
                  return options;
                }
                return options?.filter(
                  (option: any) =>
                    option?.label?.toLowerCase()?.includes(inputValue) ||
                    option?.group?.toLowerCase()?.includes(inputValue),
                );
              }}
            />
          )}
        </div>

        {isCrmApp && (
          <div className={scssClass.fieldGroup}>
            <Typography className={scssClass.label}>
              Associated Groups
            </Typography>
            <Dropdown
              placeholder="Select"
              fullWidth
              optionsTextLimit={200}
              style={{ width: "100%" }}
              options={associatedGroupOptions}
              selectedOptions={associatedGroupSelectedOptions}
              onChange={handleAssociatedGroupChange}
              type={DropdownType.FilterMultiSelect}
              disableCloseOnSelect
              selectAllText={"All"}
              isOptionEqualToValue={(option: any, value: any) =>
                option?.id === value?.id
              }
              renderOption={createMultiSelectRenderOption(
                associatedGroupSelectedOptions,
                handleAssociatedGroupChange,
              )}
              filterOptions={(options: any, state: any) => {
                const inputValue = state?.inputValue?.toLowerCase();
                if (!inputValue) {
                  return options;
                }
                return options?.filter(
                  (option: any) =>
                    option?.label?.toLowerCase()?.includes(inputValue) ||
                    option?.group?.toLowerCase()?.includes(inputValue),
                );
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateEditRoleFeatureSidePanel;
